import { FC } from "react";
import { InputSize, Show } from "shared/components";
import { Check } from "untitledui-js-base";
import { ApplicantType } from "shared/types";
import { Select } from "shared/components";
import { cx } from "utils/classnames";

interface Props {
  id: string;
  isDisabled?: boolean;
  onChange: (v: ApplicantType) => void;
  selectedRole: string;
  className?: string;
  size: InputSize;
  disabledRoles?: ApplicantType[];
}

const TitleWithDesc = (
  labelText: string,
  descriptionText: string,
  isDisabled: boolean
) => (
  <div className={cx("p-4 space-y-2", isDisabled ? "text-gray-400" : "")}>
    <h3
      className={cx(
        "font-medium text-sm",
        isDisabled ? "text-content-100" : "text-content-1000"
      )}
    >
      {labelText}
    </h3>
    <p
      className={cx(
        "text-xs",
        isDisabled ? "text-content-100" : "text-content-400"
      )}
    >
      {descriptionText}
    </p>
  </div>
);

const roles = [
  {
    label: TitleWithDesc(
      "Applicant",
      "Anyone over 18 living in the property, such as a roommate, spouse, or partner",
      false
    ),
    value: ApplicantType.Applicant,
    isDisabled: false,
    disabledMessage: ""
  },
  {
    label: TitleWithDesc(
      "Guarantor",
      "A person who won't live in the property but will provide financial support",
      false
    ),
    value: ApplicantType.CoSigner,
    isDisabled: false,
    disabledMessage:
      "Guarantor role has been disabled in the screening template settings"
  }
];

export const RoleSelect: FC<Props> = ({
  id,
  isDisabled,
  onChange,
  selectedRole,
  className,
  size,
  disabledRoles = []
}) => {
  const updatedRoles = roles.map((role) => ({
    ...role,
    isDisabled: disabledRoles.includes(role.value),
    label: TitleWithDesc(
      role.value === ApplicantType.Applicant ? "Applicant" : "Guarantor",
      role.value === ApplicantType.Applicant
        ? "Anyone over 18 living in the property, such as a roommate, spouse, or partner"
        : "A person who won't live in the property but will provide financial support",
      disabledRoles.includes(role.value)
    )
  }));

  return (
    <Select
      id={id}
      className={className}
      menuPosition="fixed"
      size={size}
      value={selectedRole}
      isDisabled={isDisabled}
      options={updatedRoles}
      onChange={(e) => onChange(e.target.value as ApplicantType)}
      styles={{
        menu: (defaultStyles) => ({
          ...defaultStyles,
          width: 376,
          right: 0
        }),
        menuList: (defaultStyles) => ({
          ...defaultStyles,
          borderRadius: 6,
          padding: 0
        }),
        option: (defaultStyles, { isFocused, isDisabled, isSelected }) => ({
          ...defaultStyles,
          padding: 0,
          cursor: isDisabled ? "not-allowed" : "pointer",
          backgroundColor:
            isDisabled || (isFocused && !isSelected)
              ? "rgba(28, 29, 34, 0.04)"
              : defaultStyles.color
        })
      }}
      formatOptionLabel={(option, { context }) => {
        if (!("value" in option)) return option.label;
        const { label, value } = option;

        return context === "menu" ? (
          <div className="flex items-center justify-between">
            {label}
            <Show if={value === selectedRole}>
              <Check className="stroke-primary-500 mr-4" />
            </Show>
          </div>
        ) : value === ApplicantType.CoSigner ? (
          "Guarantor"
        ) : (
          "Applicant"
        );
      }}
    />
  );
};
